import React, { useState } from "react";
import {
	Box,
	Paper,
	Typography,
	Button,
	Avatar,
	Alert,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	CircularProgress,
	List,
	ListItem,
	ListItemAvatar,
	ListItemText,
	IconButton,
	TextField
} from "@mui/material";
import { Close as CloseIcon, Send as SendIcon, Bookmark as BookmarkIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FreelancerProfile } from "../../../models/FreelancerProfile";
import { useEnvironmentNavigation } from "../../../hooks/useEnvironmentNavigation";
import { useBulkInviteFreelancers } from "../../../hooks/useProjectFreelancerProfiles";
import { useCreateTalentSelection } from "../../../hooks/useAnonymizedTalents";
import { zoullooColors } from "../../../theme";
import { stringAvatar } from "../../../utils/avatarUtils";

interface SelectedTalentsCartProps {
	selectedTalents: FreelancerProfile[];
	projectId?: number;
	onRemoveTalent: (talentId: number) => void;
	onClearSelection: () => void;
	onTalentsSubmitted?: () => void;
}

export const SelectedTalentsCart: React.FC<SelectedTalentsCartProps> = ({
	selectedTalents,
	projectId,
	onRemoveTalent,
	onClearSelection,
	onTalentsSubmitted
}) => {
	const { t } = useTranslation();
	const { navigateByEnvironment } = useEnvironmentNavigation();
	const navigate = useNavigate();
	const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [submitSuccess, setSubmitSuccess] = useState(false);

	// "Save as anonymized public-link selection" (prospect flow)
	const [saveDialogOpen, setSaveDialogOpen] = useState(false);
	const [selectionTitle, setSelectionTitle] = useState("");
	const [saveError, setSaveError] = useState<string | null>(null);

	const bulkInviteMutation = useBulkInviteFreelancers();
	const createSelectionMutation = useCreateTalentSelection();

	const handleSaveSelection = async () => {
		setSaveError(null);
		const trimmed = selectionTitle.trim();
		if (!trimmed) {
			setSaveError(t("anonymizedTalent.saveDialogTitleRequired"));
			return;
		}
		if (selectedTalents.length === 0) {
			setSaveError(t("anonymizedTalent.saveDialogEmptyCart"));
			return;
		}

		try {
			const result = await createSelectionMutation.mutateAsync({
				title: trimmed,
				freelancerProfileIds: selectedTalents.map((t) => t.id)
			});
			setSaveDialogOpen(false);
			setSelectionTitle("");
			onClearSelection();
			// Bring the CSM straight to the new selection's detail so they can copy links right away.
			navigate(`/talent-selections/${result.selectionId}`);
		} catch (err) {
			setSaveError(
				err instanceof Error
					? err.message
					: t("anonymizedTalent.saveDialogGenericError")
			);
		}
	};

	const handleSubmit = async () => {
		if (!projectId) return;
		setSubmitting(true);
		setSubmitError(null);

		try {
			const freelancerProfileIds = selectedTalents.map(t => t.id);

			await bulkInviteMutation.mutateAsync({
				projectId,
				freelancerProfileIds
			});

			setSubmitSuccess(true);
			setConfirmDialogOpen(false);

			// Clear selection after successful submission
			setTimeout(() => {
				onClearSelection();
				setSubmitSuccess(false);
				if (onTalentsSubmitted) {
					onTalentsSubmitted();
				}
			}, 2000);
		} catch (error) {
			console.error("Error submitting talents:", error);
			setSubmitError(t("project.talentSelection.submitError"));
		} finally {
			setSubmitting(false);
		}
	};

	const handleOpenConfirmDialog = () => {
		setConfirmDialogOpen(true);
		setSubmitError(null);
	};

	const handleCloseConfirmDialog = () => {
		if (!submitting) {
			setConfirmDialogOpen(false);
			setSubmitError(null);
		}
	};

	return (
		<>
			{/* Title outside the card like in PDF */}
			<Typography variant="h6" sx={{ mb: 2 }}>
				{t("project.talentSelection.selectedTalentsCount", {
					count: selectedTalents.length
				})}
			</Typography>

			<Paper
				elevation={1}
				sx={{
					p: 3,
					border: `1px solid ${zoullooColors.grey[400]}`,
					borderRadius: 4
				}}
			>
				{submitSuccess && (
					<Alert severity="success" sx={{ mb: 2 }}>
						{t("project.talentSelection.submitSuccess")}
					</Alert>
				)}

				{/* Selected talents display - photos in a row */}
				<Box sx={{ mb: 3 }}>
					<Box
						sx={{ display: "flex", gap: 3, flexWrap: "wrap", justifyContent: "center" }}
					>
						{selectedTalents.map(talent => (
							<Box
								key={talent.id}
								sx={{
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									position: "relative",
									minWidth: 100
								}}
							>
								{/* Remove button */}
								<IconButton
									onClick={() => onRemoveTalent(talent.id)}
									size="small"
									sx={{
										position: "absolute",
										top: -8,
										right: -8,
										bgcolor: "white",
										border: "1px solid #E0E0E0",
										zIndex: 1,
										"&:hover": {
											bgcolor: "#f5f5f5"
										}
									}}
								>
									<CloseIcon fontSize="small" />
								</IconButton>

								{/* Avatar without border */}
								<Avatar
									{...stringAvatar(
										`${talent.freelancer?.user?.firstName} ${talent.freelancer?.user?.lastName}`
									)}
									src={
										talent.freelancer?.picture?.urlPath
											? `${process.env.REACT_APP_ZOULLOO_FILE_STORAGE_BASE_URL}/${process.env.REACT_APP_FREELANCER_PICTURES_CONTAINER_NAME}/${talent.freelancer?.picture?.urlPath}`
											: undefined
									}
									sx={{
										width: 60,
										height: 60,
										mb: 1,
										border: "none"
									}}
								/>

								{/* Name - Clickable */}
								<Box
									onClick={() =>
										navigateByEnvironment(`/applications/talent/${talent.key}`)
									}
									sx={{
										cursor: "pointer",
										"&:hover": {
											"& .talent-name": {
												color: zoullooColors.secondary.main,
												textDecoration: "underline"
											}
										}
									}}
								>
									<Typography
										className="talent-name"
										variant="body2"
										sx={{
											fontWeight: 500,
											textAlign: "center",
											transition: "color 0.2s ease, text-decoration 0.2s ease"
										}}
									>
										{talent.freelancer?.user?.firstName}
									</Typography>
									<Typography
										className="talent-name"
										variant="body2"
										sx={{
											fontWeight: 500,
											textAlign: "center",
											transition: "color 0.2s ease, text-decoration 0.2s ease"
										}}
									>
										{talent.freelancer?.user?.lastName}
									</Typography>
								</Box>

								{/* Expertise and experience */}
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ textAlign: "center", mt: 0.5 }}
								>
									{talent.expertise?.label && t(`Expertises.${talent.expertise.label}`)}
								</Typography>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ textAlign: "center" }}
								>
									{talent.experience?.label &&
										t(`Experiences.${talent.experience.label}`)}
								</Typography>
							</Box>
						))}
					</Box>
				</Box>

				{/* Action buttons centered on separate line */}
				<Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
					{projectId && (
						<Button
							variant="contained"
							onClick={handleOpenConfirmDialog}
							disabled={selectedTalents.length === 0}
							sx={{
								bgcolor: zoullooColors.secondary.main,
								color: "white",
								px: 3,
								"&:hover": {
									bgcolor: zoullooColors.secondary.dark
								}
							}}
						>
							{t("project.talentSelection.sendToTalents")}
						</Button>
					)}
					{/*
					  Save as anonymized public-link selection — ONLY in the prospecting
					  flow (no project). Inside a project the cart is about inviting
					  talents to that project, so we hide the "save selection" action.
					*/}
					{!projectId && (
						<Button
							variant="outlined"
							startIcon={<BookmarkIcon />}
							onClick={() => {
								setSaveError(null);
								setSelectionTitle("");
								setSaveDialogOpen(true);
							}}
							disabled={selectedTalents.length === 0}
							sx={{
								px: 3,
								borderColor: zoullooColors.primary.main,
								color: zoullooColors.primary.main,
								"&:hover": {
									borderColor: zoullooColors.primary.dark,
									bgcolor: zoullooColors.proposal.seashell
								}
							}}
						>
							{t("anonymizedTalent.saveButton")}
						</Button>
					)}
					<Button
						variant="outlined"
						onClick={onClearSelection}
						sx={{
							px: 3,
							borderColor: zoullooColors.secondary.main,
							color: zoullooColors.secondary.main,
							"&:hover": {
								borderColor: zoullooColors.secondary.dark,
								bgcolor: zoullooColors.secondary.light + "10"
							}
						}}
					>
						{t("project.talentSelection.deleteSelection")}
					</Button>
				</Box>
			</Paper>

			{/* Confirmation Dialog (only in project mode) */}
			{projectId && <Dialog
				open={confirmDialogOpen}
				onClose={handleCloseConfirmDialog}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>{t("project.talentSelection.confirmSubmitTitle")}</DialogTitle>
				<DialogContent>
					{submitError && (
						<Alert severity="error" sx={{ mb: 2 }}>
							{submitError}
						</Alert>
					)}

					<Typography variant="body1" sx={{ mb: 2 }}>
						{t("project.talentSelection.confirmSubmitMessage", {
							count: selectedTalents.length
						})}
					</Typography>

					<List>
						{selectedTalents.map(talent => (
							<ListItem
								key={talent.id}
								sx={{
									cursor: "pointer",
									"&:hover": {
										bgcolor: "rgba(0, 0, 0, 0.04)"
									}
								}}
								onClick={() =>
									navigateByEnvironment(`/applications/talent/${talent.key}`)
								}
							>
								<ListItemAvatar>
									<Avatar
										{...stringAvatar(
											`${talent.freelancer?.user?.firstName} ${talent.freelancer?.user?.lastName}`
										)}
										src={
											talent.freelancer?.picture?.urlPath
												? `${process.env.REACT_APP_ZOULLOO_FILE_STORAGE_BASE_URL}/${process.env.REACT_APP_FREELANCER_PICTURES_CONTAINER_NAME}/${talent.freelancer?.picture?.urlPath}`
												: undefined
										}
									>
										{talent.freelancer?.user?.firstName?.charAt(0)}
										{talent.freelancer?.user?.lastName?.charAt(0)}
									</Avatar>
								</ListItemAvatar>
								<ListItemText
									primary={`${talent.freelancer?.user?.firstName} ${talent.freelancer?.user?.lastName}`}
									secondary={`${talent.expertise?.label || ""} - ${
										talent.experience?.label || ""
									}`}
									sx={{
										"& .MuiListItemText-primary": {
											color: zoullooColors.secondary.main,
											fontWeight: 500
										}
									}}
								/>
							</ListItem>
						))}
					</List>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseConfirmDialog} disabled={submitting}>
						{t("common.cancel")}
					</Button>
					<Button
						onClick={handleSubmit}
						variant="contained"
						disabled={submitting}
						startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
						sx={{
							bgcolor: zoullooColors.secondary.main,
							color: "white",
							"&:hover": {
								bgcolor: zoullooColors.secondary.dark
							}
						}}
					>
						{submitting ? t("common.submitting") : t("common.submit")}
					</Button>
				</DialogActions>
			</Dialog>}

			{/* Save selection dialog — Option B: generate one public link per talent */}
			<Dialog
				open={saveDialogOpen}
				onClose={() => !createSelectionMutation.isPending && setSaveDialogOpen(false)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>{t("anonymizedTalent.saveDialogTitle")}</DialogTitle>
				<DialogContent>
					{saveError && (
						<Alert severity="error" sx={{ mb: 2 }}>
							{saveError}
						</Alert>
					)}
					<Typography variant="body2" sx={{ mb: 2, color: zoullooColors.text.secondary }}>
						{t("anonymizedTalent.saveDialogIntro", { count: selectedTalents.length })}
					</Typography>
					<TextField
						autoFocus
						fullWidth
						label={t("anonymizedTalent.saveDialogTitleLabel")}
						placeholder={t("anonymizedTalent.saveDialogTitlePlaceholder")}
						value={selectionTitle}
						onChange={(e) => setSelectionTitle(e.target.value)}
						disabled={createSelectionMutation.isPending}
						inputProps={{ maxLength: 200 }}
					/>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setSaveDialogOpen(false)}
						disabled={createSelectionMutation.isPending}
					>
						{t("anonymizedTalent.saveDialogCancel")}
					</Button>
					<Button
						onClick={handleSaveSelection}
						variant="contained"
						disabled={createSelectionMutation.isPending}
						startIcon={
							createSelectionMutation.isPending ? (
								<CircularProgress size={20} />
							) : (
								<BookmarkIcon />
							)
						}
						sx={{
							bgcolor: zoullooColors.primary.main,
							color: "white",
							"&:hover": { bgcolor: zoullooColors.primary.dark }
						}}
					>
						{t("anonymizedTalent.saveDialogSave")}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
